import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
    if (!obj) return obj;
    return {
        id: obj?.id,
        quoteNumber: obj?.quote_number,
        requestType: obj?.request_type,
        referenceId: obj?.reference_id,
        userId: obj?.user_id,
        customerName: obj?.customer_name,
        customerEmail: obj?.customer_email,
        customerPhone: obj?.customer_phone,
        requestDetails: obj?.request_details,
        subtotal: obj?.subtotal,
        taxAmount: obj?.tax_amount,
        totalAmount: obj?.total_amount,
        currency: obj?.currency,
        status: obj?.status,
        notes: obj?.notes,
        adminNotes: obj?.admin_notes,
        createdAt: obj?.created_at,
        updatedAt: obj?.updated_at,
        processedAt: obj?.processed_at,
        processedBy: obj?.processed_by,
    };
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
    return {
        quote_number: obj?.quoteNumber,
        request_type: obj?.requestType,
        reference_id: obj?.referenceId,
        user_id: obj?.userId,
        customer_name: obj?.customerName,
        customer_email: obj?.customerEmail,
        customer_phone: obj?.customerPhone,
        request_details: obj?.requestDetails,
        subtotal: obj?.subtotal,
        tax_amount: obj?.taxAmount,
        total_amount: obj?.totalAmount,
        currency: obj?.currency,
        status: obj?.status,
        notes: obj?.notes,
        admin_notes: obj?.adminNotes,
        processed_at: obj?.processedAt,
        processed_by: obj?.processedBy,
    };
};

export const quoteRequestService = {
    async createQuoteRequest(quoteRequest) {
        try {
            // Check if user is authenticated
            const { data: { user } } = await supabase?.auth?.getUser();

            // Generate quote number
            const { data: quoteNumber } = await supabase?.rpc('generate_quote_request_number');

            const { data, error } = await supabase
                ?.from('quote_requests')
                ?.insert({
                    ...toSnakeCase(quoteRequest),
                    quote_number: quoteNumber,
                    user_id: user?.id || null,
                })
                ?.select()
                ?.single();

            if (error) throw error;

            const createdRequest = toCamelCase(data);

            // Send email notification to customer
            try {
                await emailService?.sendQuoteRequestConfirmation(createdRequest);
            } catch (emailError) {
                console.error('Failed to send quote request confirmation email:', emailError);
                // Don't throw - request was created successfully
            }

            return createdRequest;
        } catch (error) {
            console.error('Error creating quote request:', error);
            throw error;
        }
    },

    async getAllQuoteRequests(filters = {}) {
        try {
            let query = supabase
                ?.from('quote_requests')
                ?.select('*');

            // Apply filters
            if (filters?.status) {
                query = query?.eq('status', filters?.status);
            }

            if (filters?.requestType) {
                query = query?.eq('request_type', filters?.requestType);
            }

            if (filters?.search) {
                query = query?.or(`customer_name.ilike.%${filters?.search}%,customer_email.ilike.%${filters?.search}%,quote_number.ilike.%${filters?.search}%`);
            }

            if (filters?.startDate) {
                query = query?.gte('created_at', filters?.startDate);
            }

            if (filters?.endDate) {
                query = query?.lte('created_at', filters?.endDate);
            }

            query = query?.order('created_at', { ascending: false });

            const { data, error } = await query;

            if (error) throw error;
            return data?.map(toCamelCase) || [];
        } catch (error) {
            console.error('Error fetching quote requests:', error);
            throw error;
        }
    },

    async getQuoteRequestById(id) {
        try {
            const { data, error } = await supabase
                ?.from('quote_requests')
                ?.select('*')
                ?.eq('id', id)
                ?.single();

            if (error) throw error;
            return toCamelCase(data);
        } catch (error) {
            console.error('Error fetching quote request:', error);
            throw error;
        }
    },

    async updateQuoteRequestStatus(id, status, adminNotes = null) {
        try {
            const { data: { user } } = await supabase?.auth?.getUser();

            const updateData = {
                status,
                processed_at: new Date()?.toISOString(),
                processed_by: user?.id,
            };

            if (adminNotes) {
                updateData.admin_notes = adminNotes;
            }

            const { data, error } = await supabase
                ?.from('quote_requests')
                ?.update(updateData)
                ?.eq('id', id)
                ?.select()
                ?.single();

            if (error) throw error;

            const updatedRequest = toCamelCase(data);

            // Send email notification based on status
            try {
                if (status === 'accepted') {
                    await emailService?.sendQuoteRequestAccepted(updatedRequest);
                } else if (status === 'rejected') {
                    await emailService?.sendQuoteRequestRejected(updatedRequest);
                }
            } catch (emailError) {
                console.error('Failed to send status update email:', emailError);
            }

            return updatedRequest;
        } catch (error) {
            console.error('Error updating quote request status:', error);
            throw error;
        }
    },

    async deleteQuoteRequest(id) {
        try {
            const { error } = await supabase
                ?.from('quote_requests')
                ?.delete()
                ?.eq('id', id);

            if (error) throw error;
        } catch (error) {
            console.error('Error deleting quote request:', error);
            throw error;
        }
    },

    async getQuoteRequestStats() {
        try {
            const { data, error } = await supabase
                ?.from('quote_requests')
                ?.select('status, request_type');

            if (error) throw error;

            const stats = {
                total: data?.length || 0,
                pending: data?.filter(r => r?.status === 'pending')?.length || 0,
                contacted: data?.filter(r => r?.status === 'contacted')?.length || 0,
                accepted: data?.filter(r => r?.status === 'accepted')?.length || 0,
                rejected: data?.filter(r => r?.status === 'rejected')?.length || 0,
                completed: data?.filter(r => r?.status === 'completed')?.length || 0,
                vehicleBookings: data?.filter(r => r?.request_type === 'vehicle_booking')?.length || 0,
                propertyRentals: data?.filter(r => r?.request_type === 'property_rental')?.length || 0,
            };

            return stats;
        } catch (error) {
            console.error('Error fetching quote request stats:', error);
            throw error;
        }
    },

    subscribeToQuoteRequests(callback) {
        const channel = supabase
            ?.channel('quote_requests')
            ?.on('postgres_changes', { event: '*', schema: 'public', table: 'quote_requests' }, (payload) => {
                callback(payload);
            })
            ?.subscribe();

        return () => supabase?.removeChannel(channel);
    },

    formatAmount(amount, currency = 'CDF') {
        if (currency === 'CDF') {
            return `${amount?.toLocaleString()} CDF`;
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        })?.format(amount);
    },

    getStatusLabel(status) {
        const labels = {
            pending: 'En attente',
            contacted: 'Contacté',
            accepted: 'Accepté',
            rejected: 'Refusé',
            completed: 'Terminé',
        };
        return labels?.[status] || status;
    },

    getStatusColor(status) {
        const colors = {
            pending: 'text-yellow-600 bg-yellow-100',
            contacted: 'text-blue-600 bg-blue-100',
            accepted: 'text-green-600 bg-green-100',
            rejected: 'text-red-600 bg-red-100',
            completed: 'text-gray-600 bg-gray-100',
        };
        return colors?.[status] || 'text-gray-600 bg-gray-100';
    },

    getTypeLabel(type) {
        const labels = {
            vehicle_booking: 'Location de Véhicule',
            property_rental: 'Location Immobilière',
        };
        return labels?.[type] || type;
    },
};
